import { useNavigate } from 'react-router-dom'

import { Box, Icon, Section, Text } from '@factorialco/deprecated-design-system'
import capitalize from 'lodash/capitalize'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import type SubscriptionAddon from 'modules/billing/store/models/SubscriptionAddon'
import getTitle from 'modules/documents/components/Documents/getTitle'
import folders from 'modules/documents/store/collections/folders'

interface Props {
  addon: SubscriptionAddon
}
interface ContentProps {
  addon: SubscriptionAddon
}

const Content = ({ addon }: ContentProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { loading } = useUnsafeAsyncData(
    () =>
      folders.fetch({
        data: {
          billing_subscription_addon_id: addon.attributes.db_id,
          active: true,
        },
      }),
    [addon]
  )

  return (
    <>
      {loading && <Loading />}
      {!loading && (
        <Box
          gap="s8"
          flexDirection="column"
          alignItems="center"
          justifyContent="spaceBetween"
          border={{
            all: {
              color: 'grey400',
              width: 's1',
              style: 'solid',
            },
          }}
          borderRadius={{
            all: 'abs016',
          }}
        >
          {folders.map((folder) => {
            const isLastElement = folder === folders.slice().pop()

            const folderDirectory = () => {
              if (folder.attributes.is_employee) {
                return i18n.t('folders.directory.employee')
              }

              if (folder.attributes.is_company) {
                return i18n.t('folders.directory.company')
              }

              return ''
            }

            const handleNavigation = () => {
              return folder.isCompany
                ? navigate(
                    navigation.settings.folders.internal.show.fullPath({
                      id: folder.attributes.id,
                    })
                  )
                : navigate(
                    navigation.settings.folders.employees.show.fullPath({
                      id: folder.attributes.id,
                    })
                  )
            }

            return (
              <Box
                cursor="pointer"
                onClick={handleNavigation}
                key={folder.attributes.id}
                flexDirection="row"
                justifyContent="spaceBetween"
                width="full"
                padding="s16"
                border={
                  !isLastElement && {
                    bottom: {
                      color: 'grey400',
                      width: 's1',
                      style: 'solid',
                    },
                  }
                }
                gap="s8"
                alignItems="center"
              >
                <Box alignItems="flexStart" flexDirection="column" gap="s12">
                  <Text.Normal weight="medium" color="grey1000">
                    {capitalize(getTitle(folder.attributes.name))}
                  </Text.Normal>

                  <Text.Normal capitalize>{capitalize(folderDirectory())}</Text.Normal>
                </Box>

                <Box
                  padding="s2"
                  border={{
                    all: {
                      color: 'grey400',
                      width: 's1',
                      style: 'solid',
                    },
                  }}
                  borderRadius={{
                    all: 'rel500',
                  }}
                >
                  <Icon.Medium icon="arrow-right" />
                </Box>
              </Box>
            )
          })}
        </Box>
      )}
    </>
  )
}

const FoldersSettings = ({ addon }: Props) => {
  return (
    <Section
      title={i18n.t('app_store_v2.apps.payroll_bundle.settings.folders.title')}
      illustration="folder-file"
      description={i18n.t('app_store_v2.apps.payroll_bundle.settings.folders.description')}
    >
      <Content addon={addon} />
    </Section>
  )
}

export default observer(FoldersSettings)
